import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import {
    DropdownMenu,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator,
    DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { ConversationParticipant, ParticipantRole } from '@/types/groupChat'
import { MoreVertical, UserMinus, UserX, Shield, Users } from 'lucide-react'
import RoleBadge from './RoleBadge'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

interface ParticipantListProps {
    participants: ConversationParticipant[]
    currentUserId: string
    currentUserRole: ParticipantRole
    onRemoveParticipant?: (userId: string) => void
    onKickParticipant?: (userId: string) => void
    onChangeRole?: (userId: string, newRole: ParticipantRole) => void
    showInactive?: boolean
}

/**
 * ParticipantList Component
 * Displays list of group chat participants with their roles and status
 * Provides action menu for participant management based on permissions
 */
export default function ParticipantList({
    participants,
    currentUserId,
    currentUserRole,
    onRemoveParticipant,
    onKickParticipant,
    onChangeRole,
    showInactive = false,
}: ParticipantListProps) {
    const { t } = useTranslation('common')
    const [filter, setFilter] = useState<'all' | 'active'>('active')

    // Filter participants based on status
    const filteredParticipants = participants.filter((p) => {
        if (filter === 'active') {
            return p.status === 'ACTIVE'
        }
        return true
    })

    // Check if current user can perform actions
    const canManageParticipants = currentUserRole === 'ADMIN' || currentUserRole === 'MODERATOR'
    const canChangeRoles = currentUserRole === 'ADMIN'

    // Get initials from user name
    const getInitials = (participant: ConversationParticipant) => {
        const { firstname, lastname } = participant.user
        return `${firstname.charAt(0)}${lastname.charAt(0)}`.toUpperCase()
    }

    // Get avatar URL
    const getAvatarUrl = (participant: ConversationParticipant) => {
        if (participant.user.avatar) {
            return participant.user.avatar
        }
        const fullName = `${participant.user.firstname} ${participant.user.lastname}`
        return `https://ui-avatars.com/api/?name=${encodeURIComponent(fullName)}&size=128&background=random`
    }

    return (
        <div className="flex flex-col h-full bg-white">
            {/* Header with filter toggle */}
            <div className="p-4 border-b border-gray-200 bg-white sticky top-0 z-10">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-semibold text-base text-gray-900">
                            {t('chat.participantList.title')}
                        </h3>
                        <p className="text-sm text-gray-500 mt-0.5">
                            {filteredParticipants.length} {filteredParticipants.length === 1 ? t('chat.participantList.member') : t('chat.participantList.members')}
                        </p>
                    </div>
                    {showInactive && (
                        <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setFilter(filter === 'active' ? 'all' : 'active')}
                            className="text-xs h-8"
                        >
                            {filter === 'active' ? t('chat.participantList.showAll') : t('chat.participantList.activeOnly')}
                        </Button>
                    )}
                </div>
            </div>

            {/* Participant list */}
            <div className="flex-1 overflow-y-auto">
                {filteredParticipants.map((participant) => {
                    const isCurrentUser = participant.userId === currentUserId
                    const canActOnParticipant =
                        !isCurrentUser &&
                        canManageParticipants &&
                        participant.status === 'ACTIVE' &&
                        // Moderators can't remove admins
                        !(currentUserRole === 'MODERATOR' && participant.role === 'ADMIN')

                    return (
                        <div
                            key={participant.id}
                            className="flex items-center justify-between p-4 hover:bg-gray-50 transition-colors border-b border-gray-100 last:border-b-0"
                        >
                            <div className="flex items-center space-x-3 flex-1 min-w-0">
                                <Avatar className="h-11 w-11 ring-2 ring-gray-100">
                                    <AvatarImage src={getAvatarUrl(participant)} />
                                    <AvatarFallback className="bg-gradient-to-br from-blue-500 to-purple-500 text-white font-semibold">
                                        {getInitials(participant)}
                                    </AvatarFallback>
                                </Avatar>

                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <p className="font-medium text-sm text-gray-900 truncate">
                                            {participant.user.firstname} {participant.user.lastname}
                                        </p>
                                        {isCurrentUser && (
                                            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                                                {t('chat.participantList.you')}
                                            </span>
                                        )}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <RoleBadge role={participant.role} />
                                        {participant.status !== 'ACTIVE' && (
                                            <span className="text-xs text-gray-500">
                                                {participant.status === 'LEFT' ? t('chat.participantList.left') : t('chat.participantList.removed')}
                                            </span>
                                        )}
                                    </div>
                                </div>
                            </div>

                            {/* Action menu */}
                            {canActOnParticipant && (
                                <DropdownMenu>
                                    <DropdownMenuTrigger asChild>
                                        <Button 
                                            variant="ghost" 
                                            size="sm" 
                                            className="h-8 w-8 p-0 hover:bg-gray-200"
                                        >
                                            <MoreVertical className="h-4 w-4 text-gray-600" />
                                        </Button>
                                    </DropdownMenuTrigger>
                                    <DropdownMenuContent align="end" className="w-48">
                                        {/* Change role (admin only) */}
                                        {canChangeRoles && onChangeRole && (
                                            <>
                                                <DropdownMenuItem
                                                    onClick={() => {
                                                        const newRole =
                                                            participant.role === 'MEMBER'
                                                                ? 'MODERATOR'
                                                                : participant.role === 'MODERATOR'
                                                                    ? 'ADMIN'
                                                                    : 'MEMBER'
                                                        onChangeRole(participant.userId, newRole)
                                                    }}
                                                    className="cursor-pointer"
                                                >
                                                    <Shield className="h-4 w-4 mr-2" />
                                                    {t('chat.participantList.changeRole')}
                                                </DropdownMenuItem>
                                                <DropdownMenuSeparator />
                                            </>
                                        )}

                                        {/* Remove participant */}
                                        {onRemoveParticipant && (
                                            <DropdownMenuItem
                                                onClick={() => onRemoveParticipant(participant.userId)}
                                                className="cursor-pointer"
                                            >
                                                <UserMinus className="h-4 w-4 mr-2" />
                                                {t('chat.participantList.remove')}
                                            </DropdownMenuItem>
                                        )}

                                        {/* Kick participant */}
                                        {onKickParticipant && (
                                            <DropdownMenuItem
                                                onClick={() => onKickParticipant(participant.userId)}
                                                className="text-red-600 cursor-pointer focus:text-red-600 focus:bg-red-50"
                                            >
                                                <UserX className="h-4 w-4 mr-2" />
                                                {t('chat.participantList.kick')}
                                            </DropdownMenuItem>
                                        )}
                                    </DropdownMenuContent>
                                </DropdownMenu>
                            )}
                        </div>
                    )
                })}

                {filteredParticipants.length === 0 && (
                    <div className="p-12 text-center">
                        <Users className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                        <p className="text-sm text-gray-500 font-medium">{t('chat.participantList.noParticipants')}</p>
                        <p className="text-xs text-gray-400 mt-1">
                            {filter === 'active' ? t('chat.participantList.tryShowingAll') : t('chat.participantList.noParticipantsInGroup')}
                        </p>
                    </div>
                )}
            </div>
        </div>
    )
}
